import React from "react"
import { parseISO, differenceInMinutes, format } from "date-fns"

// Default period duration in minutes (1 hour)
const DEFAULT_PERIOD_DURATION_MINUTES = 60

/**
 * Calculate the row span for a leave item based on its start and end times.
 *
 * @param {Date|string} startAt - The start time of the leave
 * @param {Date|string} endAt - The end time of the leave
 * @param {number} periodDurationMinutes - Duration of each time period in minutes
 * @returns {number} The number of rows the leave should span (minimum 1)
 */
function calculateLeaveRowSpan(startAt, endAt, periodDurationMinutes) {
  if (!startAt || !endAt || !periodDurationMinutes || periodDurationMinutes <= 0) {
    return 1
  }

  const startDate = typeof startAt === "string" ? parseISO(startAt) : startAt
  const endDate = typeof endAt === "string" ? parseISO(endAt) : endAt

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 1
  }

  const durationMinutes = differenceInMinutes(endDate, startDate)

  if (durationMinutes <= 0) {
    return 1
  }

  return Math.max(1, Math.ceil(durationMinutes / periodDurationMinutes))
}

/**
 * Format a time for display (e.g., "9:00 AM")
 *
 * @param {Date|string} dateTime - The date/time to format
 * @returns {string|null} Formatted time string or null if invalid
 */
function formatTime(dateTime) {
  if (!dateTime) return null

  try {
    const date = typeof dateTime === "string" ? parseISO(dateTime) : dateTime
    if (Number.isNaN(date.getTime())) return null
    return format(date, "h:mm a")
  } catch {
    return null
  }
}

/**
 * Get the CSS class modifier based on leave type
 *
 * @param {string} leaveType - The type of leave
 * @returns {string} CSS class modifier
 */
function getLeaveTypeClass(leaveType) {
  if (!leaveType) return ""

  const type = leaveType.toLowerCase()
  if (type.includes("annual")) return "annual-leave"
  if (type.includes("sick")) return "sick-leave"
  if (type.includes("personal")) return "personal-leave"
  return ""
}

/**
 * LeaveItem component displays a leave/block-out item in the operation schedule grid.
 * It calculates and applies row span based on the leave duration.
 *
 * @param {Object} props
 * @param {Object} props.blockOut - The block out/leave object
 * @param {string|Date} props.blockOut.start_at - Start time of the leave
 * @param {string|Date} props.blockOut.end_at - End time of the leave
 * @param {string} props.blockOut.leave_type - Type of leave (e.g., "ANNUAL LEAVE")
 * @param {number} props.periodDurationMinutes - Duration of each time period in minutes
 * @param {number} props.rowSpan - Pre-calculated row span (optional, will calculate if not provided)
 * @param {boolean} props.showTimeRange - Whether to display start and end times (default: true)
 */
export default function LeaveItem({
  blockOut,
  periodDurationMinutes = DEFAULT_PERIOD_DURATION_MINUTES,
  rowSpan: providedRowSpan,
  showTimeRange = true
}) {
  if (!blockOut) {
    return null
  }

  // Extract block out properties (handle both camelCase and snake_case)
  const { id } = blockOut
  const startAt = blockOut.start_at || blockOut.startAt
  const endAt = blockOut.end_at || blockOut.endAt
  const leaveType = blockOut.leave_type || blockOut.leaveType || "Leave"

  // Calculate row span if not provided
  const rowSpan = providedRowSpan || calculateLeaveRowSpan(startAt, endAt, periodDurationMinutes)

  // Format leave type for display (capitalize first letter of each word)
  const formatLeaveType = type => {
    if (!type) return "Leave"
    return type
      .toLowerCase()
      .split(/[\s_]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const displayLeaveType = formatLeaveType(leaveType)

  // Format time range for display
  const startTimeFormatted = formatTime(startAt)
  const endTimeFormatted = formatTime(endAt)
  const hasTimeRange = showTimeRange && startTimeFormatted && endTimeFormatted

  // Get CSS class modifier based on leave type
  const leaveTypeClass = getLeaveTypeClass(leaveType)

  // Calculate style for row spanning
  const itemStyle = {
    height: rowSpan > 1 ? `${rowSpan * 100}%` : undefined
  }

  // Build class names
  const classNames = ["schedule-leave-item", leaveTypeClass].filter(Boolean).join(" ")

  return (
    <div
      key={id}
      className={classNames}
      style={itemStyle}
      role="status"
      aria-label={`${displayLeaveType} leave${hasTimeRange ? ` from ${startTimeFormatted} to ${endTimeFormatted}` : ""}`}>
      <div className="leave-item-content">
        <div className="leave-type">{displayLeaveType}</div>
        {hasTimeRange && (
          <div className="leave-time-range">
            {startTimeFormatted}
            {" - "}
            {endTimeFormatted}
          </div>
        )}
      </div>
    </div>
  )
}

// Export the helper function for testing and reuse
export { calculateLeaveRowSpan }
